import { useState } from "react";
import axios from "axios";
import { Compass, ChevronDown, ChevronUp, Clock, Train, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase 8 — collapsed-by-default teaser for the mini-tour. Tap the
 * "Mini-Tour available" pill to expand the curated itinerary (fetched
 * on demand from `/api/layover/{iata}?duration_min=N`).
 *
 * Props: a single `layover` object as emitted by flight_agent's
 * `_annotate_layovers`. Returns null when the layover is not eligible.
 */
export default function LayoverMiniTourTeaser({ layover }) {
    const [expanded, setExpanded] = useState(false);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    if (!layover?.mini_tour_eligible) return null;

    const handleToggle = async () => {
        const next = !expanded;
        setExpanded(next);
        if (next && !plan && !loading) {
            setLoading(true);
            setErr(null);
            try {
                const r = await axios.get(`${API}${layover.mini_tour_url}`, { timeout: 8000 });
                setPlan(r.data);
            } catch (e) {
                setErr(e?.message || "failed to load");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="mt-3" data-testid={`layover-mini-tour-${layover.iata}`}>
            <button
                type="button"
                onClick={handleToggle}
                className="inline-flex items-center gap-2 rounded-full border border-[#E5E5E5] bg-white px-3 py-1.5 text-[12px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                data-testid={`mini-tour-teaser-${layover.iata}`}
                aria-expanded={expanded}
            >
                <Compass size={12} strokeWidth={2} />
                Mini-Tour available · {layover.duration_label} in {layover.city}
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {expanded && (
                <div
                    className="mt-3 rounded-xl border border-dashed border-[#E5E5E5] bg-[#F5F5F5] p-4"
                    data-testid={`mini-tour-expanded-${layover.iata}`}
                >
                    {loading && (
                        <p className="text-[12px] text-[#737373]">Loading itinerary…</p>
                    )}
                    {err && <p className="text-[12px] text-[#B91C1C]">{err}</p>}
                    {plan && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Compass size={13} strokeWidth={2} className="text-[#0A0A0A]" />
                                <h4 className="text-[14px] font-semibold tracking-tight text-[#0A0A0A]">
                                    Places to see in {plan.city}
                                </h4>
                                <span className="ml-auto text-[11px] text-[#737373]">
                                    {Math.round(plan.usable_tour_min / 60 * 10) / 10}h available
                                </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-[#737373]">
                                <span className="inline-flex items-center gap-1">
                                    <Train size={11} strokeWidth={2} />
                                    {plan.transit.to_center_min} min · {plan.transit.to_center_mode}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <ShieldCheck size={11} strokeWidth={2} />
                                    {plan.visa_note}
                                </span>
                            </div>
                            {plan.warning ? (
                                <p className="text-[12px] italic text-[#A16207]">{plan.warning}</p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {plan.recommended_itinerary.map((p, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center justify-between text-[13px] text-[#0A0A0A]"
                                            data-testid={`tour-place-${i}`}
                                        >
                                            <span>{i + 1}. {p.name}</span>
                                            <span className="flex items-center gap-1 text-[12px] text-[#737373]">
                                                <Clock size={11} strokeWidth={2} />
                                                {p.duration_min}m
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
